import { fabric } from 'fabric';
import { HistorySnapshot } from '../card-history-coordinator';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { normalizeAuthoredOverlaySerialization } from './toggle-overlay-visibility';
import styles from './canvas-history-serialization.module.scss';

// Fabric's base toObject omits interaction locks, which template objects rely on to stay non-editable.
const SERIALIZED_PROPERTIES = [
  'name',
  'data',
  'btnId',
  'selectable',
  'evented',
  'hasControls',
  'hasRotatingPoint',
  'hoverCursor',
  'editable',
  'lockMovementX',
  'lockMovementY',
  'lockRotation',
  'lockScalingFlip',
  'lockSkewingX',
];

export type CanvasObjectReviver = (serializedObject: object, instance: fabric.Object) => void;

export type EditorHelperPresentation = {
  type: string;
  name?: string;
  photoZoneId?: string | number;
  visible: boolean;
};

export type EditorPresentation = readonly EditorHelperPresentation[];

const PRESENTATION_TYPES = new Set<string>([
  CanvasDataTypes.EditableArea,
  CanvasDataTypes.StrokedMargin,
  CanvasDataTypes.PhotoZoneButton,
  CanvasDataTypes.UserZoneAddTextButton,
  CanvasDataTypes.UserZoneAddWamButton,
  CanvasDataTypes.UserZoneInstructions,
]);

export const withPreservedCanvasFrame = async (canvas: fabric.Canvas, restore: () => Promise<void>): Promise<void> => {
  const selected = canvas.getActiveObject?.();
  const selectedId: unknown = selected?.data?.ID;
  const selectedName = selected?.name;
  const selectedType = selected?.type;
  const source = canvas.getElement?.();
  const container = source?.parentElement;
  if (!source || !container) {
    await restore();
    return;
  }
  // A transient native canvas preserves the rendered frame while Fabric rebuilds its managed canvas.
  const frame = document.createElement('canvas');
  frame.width = source.width;
  frame.height = source.height;
  frame.style.cssText = source.style.cssText;
  frame.style.removeProperty('position');
  frame.style.removeProperty('pointer-events');
  frame.style.removeProperty('z-index');
  frame.className = styles['preserved-frame'];
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('data-history-frame', '');
  const context = frame.getContext('2d');
  if (!context) {
    await restore();
    return;
  }
  context.drawImage(source, 0, 0);
  const selectionLayer = canvas.getSelectionElement?.();
  if (selectionLayer && selectionLayer !== source) {
    context.drawImage(selectionLayer, 0, 0);
  }
  container.appendChild(frame);
  try {
    await restore();
    if (selected) {
      const matchingObjects = canvas
        .getObjects()
        .filter((object) =>
          selectedId != null
            ? object.data?.ID === selectedId
            : Boolean(selectedName && object.name === selectedName && object.type === selectedType),
        );
      if (matchingObjects.length === 1) {
        canvas.setActiveObject(matchingObjects[0]);
      }
    }
    canvas.renderAll();
  } finally {
    frame.remove();
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isHelperPresentation = (value: unknown): value is EditorHelperPresentation =>
  isRecord(value) &&
  typeof value.type === 'string' &&
  PRESENTATION_TYPES.has(value.type) &&
  typeof value.visible === 'boolean' &&
  (value.name === undefined || typeof value.name === 'string') &&
  (value.photoZoneId === undefined || typeof value.photoZoneId === 'string' || typeof value.photoZoneId === 'number');

export const getHistoryEditorPresentation = (snapshot: HistorySnapshot): EditorPresentation | undefined => {
  const presentation = snapshot.editorPresentation;
  if (presentation === undefined) {
    return undefined;
  }
  if (!Array.isArray(presentation) || !presentation.every(isHelperPresentation)) {
    throw new Error('Invalid editor presentation snapshot');
  }
  return presentation;
};

const normalizeUneditedTextFlags = (objects: unknown[]): void => {
  objects.forEach((object) => {
    if (!isRecord(object)) return;
    if (isRecord(object.data) && object.data.type === CanvasDataTypes.UserText) {
      if (object.data.edited === false) delete object.data.edited;
      if (object.data.isEdited === false) delete object.data.isEdited;
    }
    if (Array.isArray(object.objects)) normalizeUneditedTextFlags(object.objects);
  });
};

const hasSnapshotImage = (snapshot: HistorySnapshot, property: 'backgroundImage' | 'overlayImage'): boolean =>
  Boolean(property === 'backgroundImage' ? snapshot.backgroundImage : snapshot.overlayImage);

const hasLoadedImage = (image: unknown): boolean => {
  if (!image) {
    return false;
  }

  const fabricImage = image as fabric.Image;
  if (typeof fabricImage.getElement !== 'function') {
    return true;
  }

  const element = fabricImage.getElement() as HTMLImageElement;
  return element.complete !== false;
};

const validateBackgroundAndOverlaySlots = (canvas: fabric.Canvas, snapshot: HistorySnapshot): void => {
  if (hasSnapshotImage(snapshot, 'backgroundImage') && !hasLoadedImage(canvas.backgroundImage)) {
    throw new Error('Canvas background image did not load');
  }
  if (hasSnapshotImage(snapshot, 'overlayImage') && !hasLoadedImage(canvas.overlayImage)) {
    throw new Error('Canvas overlay image did not load');
  }
};

export const captureCanvasSnapshot = (canvas: fabric.Canvas): HistorySnapshot => {
  const serialized = canvas.toObject(SERIALIZED_PROPERTIES);
  const detachedValue = JSON.parse(JSON.stringify(serialized)) as unknown;
  if (!isRecord(detachedValue)) {
    throw new Error('Canvas serialization did not produce an object snapshot');
  }
  const detachedSnapshot = normalizeAuthoredOverlaySerialization(canvas, detachedValue);
  detachedSnapshot.editorPresentation = [];
  if (Array.isArray(detachedSnapshot.objects)) {
    normalizeUneditedTextFlags(detachedSnapshot.objects);
    detachedSnapshot.editorPresentation = detachedSnapshot.objects.flatMap((object): EditorHelperPresentation[] => {
      if (!isRecord(object) || !isRecord(object.data) || !PRESENTATION_TYPES.has(String(object.data.type))) {
        return [];
      }
      return [
        {
          type: String(object.data.type),
          ...(typeof object.name === 'string' ? { name: object.name } : {}),
          ...(typeof object.data.photoZoneId === 'string' || typeof object.data.photoZoneId === 'number'
            ? { photoZoneId: object.data.photoZoneId }
            : {}),
          visible: object.visible !== false,
        },
      ];
    });
    detachedSnapshot.objects = detachedSnapshot.objects.filter((object) => {
      if (!isRecord(object)) {
        return false;
      }
      const data = isRecord(object.data) ? object.data : undefined;
      const type = String(data?.type ?? '');
      return !data?.removableOnSave && !PRESENTATION_TYPES.has(type) && type !== CanvasDataTypes.EditableTextButton;
    });
  }
  return detachedSnapshot;
};

export const loadCanvasSnapshot = (
  canvas: fabric.Canvas,
  snapshot: HistorySnapshot,
  reviver?: CanvasObjectReviver,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (!isRecord(snapshot) || Object.keys(snapshot).length === 0) {
      reject(new Error('Invalid canvas snapshot'));
      return;
    }

    const reviverErrors: unknown[] = [];
    const wrappedReviver: CanvasObjectReviver = (serializedObject, instance) => {
      try {
        reviver?.(serializedObject, instance);
      } catch (error) {
        reviverErrors.push(error);
      }
    };

    try {
      const documentSnapshot = { ...snapshot };
      delete documentSnapshot.editorPresentation;
      canvas.loadFromJSON(
        documentSnapshot,
        () => {
          if (reviverErrors.length > 0) {
            reject(reviverErrors[0]);
            return;
          }
          try {
            validateBackgroundAndOverlaySlots(canvas, snapshot);
            resolve();
          } catch (error) {
            reject(error);
          }
        },
        wrappedReviver,
      );
    } catch (error) {
      reject(error);
    }
  });
